import { Component } from 'react';
import Table from '../../elements/Table';
import NewDomain from '../../elements/CustomDomains/NewDomain';
import Imprint from '../../elements/CustomDomains/Imprint';
import Privacy from '../../elements/CustomDomains/Privacy';
import HelpLink from '../../elements/Help/HelpLink';
import { connect } from 'react-redux';
import { FaTrash } from 'react-icons/fa';
import * as actions from '../../actions';
import Card, { CardBody, CardHeader, CardTitle } from '../../components/Card';
import { Col, Row } from 'reactstrap';
import ImprintTable from '../../elements/CustomDomains/ImprintTable';
import PrivacyTable from '../../elements/CustomDomains/PrivacyTable';
import theme from '../../assets/css/theme';
import { toUnicode } from '../../helpers/idn';
import { getTranslationKey } from '../../modules/utils/helpers';
import type { Notify } from '../../typings/redux';

interface DomainItem {
  _id: string;
  hostname: string;
  imprint?:
    | {
        id?: string;
      }
    | string;
  privacy?:
    | {
        id?: string;
      }
    | string;
}

interface CustomDomainState {
  search: string;
  itemsPerPage: number;
}

interface CustomDomainProps {
  domains: {
    all: DomainItem[];
  };
  notify: Notify;
  userType: string;
  getDomains: () => void;
  getImprints: () => void;
  getPrivacys: () => void;
  deleteDomain: (domainId: string, notify: Notify) => void;
}

class CustomDomain extends Component<CustomDomainProps, CustomDomainState> {
  columns: string[];

  columnNames: string[];

  constructor(props: CustomDomainProps) {
    super(props);

    this.state = {
      search: '',
      itemsPerPage: 10
    };

    this.columns = ['hostname', 'cname', 'imprint', 'privacy', 'action'];

    this.columnNames = [
      getTranslationKey('customDomains.domain'),
      getTranslationKey('customDomains.domainTarget'),
      getTranslationKey('customDomains.imprint'),
      getTranslationKey('customDomains.privacy'),
      getTranslationKey('dashboardTable.action')
    ];

    this.onDeleteDomain = this.onDeleteDomain.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.props.getDomains();
    this.props.getImprints();
    this.props.getPrivacys();
  }

  onDeleteDomain(domain_id: string) {
    if (window.confirm(getTranslationKey('customDomains.deleteDomainConfirmation'))) {
      this.props.deleteDomain(domain_id, this.props.notify);
    }
  }

  render() {
    const items = this.props.domains.all.map((domain) => ({
      ...domain,
      hostname: toUnicode(domain.hostname),
      imprint: (
        <Imprint
          domain={domain._id}
          selectedImprint={
            domain.imprint
              ? typeof domain.imprint === 'object'
                ? domain.imprint.id
                : domain.imprint
              : undefined
          }
        />
      ),
      privacy: (
        <Privacy
          domain={domain._id}
          selectedPrivacy={
            domain.privacy &&
            (typeof domain.privacy === 'object' ? domain.privacy.id : domain.privacy)
          }
        />
      ),
      action: (
        <FaTrash
          style={{ cursor: 'pointer' }}
          color={theme.commonColors.second}
          onClick={() => this.onDeleteDomain(domain._id)}
        />
      )
    }));

    // Note: Table component expects string but we're passing JSX for the new domain row
    // This is intentional for displaying the form inline in the table
    items.push({
      _id: 'new-domain',
      hostname: (<NewDomain />) as unknown as string,
      imprint: undefined as unknown as JSX.Element,
      privacy: undefined as unknown as JSX.Element,
      action: undefined as unknown as JSX.Element
    });

    return (
      <div className="animated fadeIn">
        <Row>
          <Col md="12" lg="12" xl="12">
            <Card>
              <CardHeader>
                <CardTitle>{getTranslationKey('customDomain')}</CardTitle>
                <div>
                  {this.props.userType === 'SB' && (
                    <HelpLink
                      href="https://support.scrappbook.de/de/collections/11561"
                      className="float-left mr-3"
                      video
                    />
                  )}
                </div>
              </CardHeader>
              <CardBody>
                <Table
                  items={items}
                  columns={this.columns}
                  columnNames={this.columnNames}
                  itemsPerPage={this.state.itemsPerPage}
                  search={this.state.search}
                  noMatchingRecords={getTranslationKey('customDomains.noMatchingRecords')}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12" lg="12" xl="6" style={{ marginTop: '30px' }}>
            <Card>
              <CardHeader>
                <CardTitle>{getTranslationKey('customDomains.imprint')}</CardTitle>
              </CardHeader>
              <CardBody>
                <ImprintTable />
              </CardBody>
            </Card>
          </Col>
          <Col md="12" lg="12" xl="6" style={{ marginTop: '30px' }}>
            <Card>
              <CardHeader>
                <CardTitle>{getTranslationKey('customDomains.privacy')}</CardTitle>
              </CardHeader>
              <CardBody>
                <PrivacyTable />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

interface RootState {
  domains: {
    all: DomainItem[];
  };
  notify: Notify;
  auth: {
    type: string;
  };
}

const mapStateToProps = ({ domains, notify, auth: { type } }: RootState) => ({
  domains,
  notify,
  userType: type
});

export default connect(mapStateToProps, actions)(CustomDomain);
